import cookies from 'js-cookie';
import { setUserVars } from '@zocdoc-frontend-common/utils-fullstory/utils';
import { isProduction } from '@zocdoc-frontend-common/utils-react';
import { VISITOR_ID_TYPES } from './constants';
import { getAbClient } from './utils/getAbClient';
import { parseSessionId } from './utils/parseSessionId';

const recordedAssignments = new Set();

/**
 * Client-side functions
 */

/**
 * Tells the AB service to record an experiment coinflip.
 * Due to usage of cookies and FullStory, this function should only be used on client-side.
 * @param {Array<Object>} experiments
 */
export const recordAssignment = (experiment) => recordAssignments([experiment]);

/**
 * Tells the AB service to record experiment assignments (aka coinflips).
 * @param {Array<Object>} experiments
 */
export const recordAssignments = (experiments) => {
    if (typeof document === 'undefined') {
        throw new Error(
            'You should not call this function on the server - use recordAssignmentWithIdentifiers instead'
        );
    }

    if (window.location.href.includes('vaccine/search')) {
        return;
    }

    return recordAssignmentsWithIdentifiers(experiments, {
        sessionId: cookies.get('bsid'),
        trackingId: cookies.get('firstTimeVisitor'),
        userAgent: navigator.userAgent,
    });
};

/**
 * Server-side functions
 */

/**
 * Tells the AB service to record an experiment coinflip with custom identifiers
 * @param {Object} experiment
 * @param {Object} identifiers
 */
export const recordAssignmentWithIdentifiers = (experiment, identifiers = {}) =>
    recordAssignmentsWithIdentifiers([experiment], identifiers);

/**
 * For use when recording assignments in an environment that does not have access
 * to document.cookies - such as during SSR
 * @param {Array<Object>} experiments
 * @param {Object} identifiers
 */
export const recordAssignmentsWithIdentifiers = (
    experiments,
    identifiers = {}
) => {
    const { sessionId, trackingId, userAgent } = identifiers;

    // Record each assignment only once
    const experimentsToRecord = experiments.filter(
        (experiment) => !recordedAssignments.has(experiment)
    );

    if (!experimentsToRecord.length) return;

    experimentsToRecord.forEach((experiment) => {
        // Throw only if these two fields are missing, because they are easily fixed.
        // If any other fields is missing, there are too many possible causes for the issue to be actionable.
        if (!experiment.experimentId)
            throw new Error('Expected experiment to have experimentId');
        if (!experiment.defaultAssignment)
            throw new Error('Expected experiment to have defaultAssignment');

        recordedAssignments.add(experiment);
    });

    const assignmentsBody = {
        experiments: experimentsToRecord.map((experiment) =>
            transformExperiment(experiment, {
                sessionId: parseSessionId(sessionId),
                trackingId,
                timestamp: new Date().toISOString(),
            })
        ),
        userAgent,
    };

    const isValid = validateAssignmentsBody(assignmentsBody);

    if (isValid && isProduction()) {
        return getAbClient()
            .setAssignmentsWithDefault(assignmentsBody)
            .then((response) => {
                if (!response.isBot) {
                    setUserVars(response.assignments);
                }
            });
    }
};

/**
 * Helper functions
 */

/**
 * Applies default values and additional properties
 * @param {Object} experiment
 * @param {string} sessionId
 * @param {string} trackingId
 * @param {string} timestamp
 */
function transformExperiment(experiment, { sessionId, trackingId, timestamp }) {
    const {
        defaultAssignment,
        experimentId,
        isFeatureFlag,
        visitorIdType = VISITOR_ID_TYPES.sessionId,
    } = experiment;

    return {
        defaultAssignment,
        experimentId,
        isFeatureFlag,
        sessionId,
        timestamp,
        trackingId,
        visitorIdType,
    };
}

var requiredAssignmentsBodyFields = ['experiments', 'userAgent'];

var requiredExperimentFields = [
    'experimentId',
    'visitorIdType',
    'defaultAssignment',
    'sessionId',
    'trackingId',
    'timestamp',
];

/**
 * Validates required fields on assignments body
 * @param {object} body
 */
function validateAssignmentsBody(body) {
    const isMissingBodyFields = requiredAssignmentsBodyFields.some(
        (field) => !body[field]
    );
    const isMissingExperimentFields = body.experiments.some((experiment) =>
        requiredExperimentFields.some((field) => !experiment[field])
    );
    const isValid = !isMissingBodyFields && !isMissingExperimentFields;

    return isValid;
}

export const clearRecordedAssignments = () => {
    recordedAssignments.clear();
};
